import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import http from "../lib/http";
import './HomePage.css';
import SEO from "../components/SEO/SEO";

const categoryGroups = {
  Materials: [
    { name: 'Bricks', icon: '🧱', color: '#FFDFD3' },
    { name: 'Cement', icon: '🏗️', color: '#FEE2B3' },
    { name: 'Tiles', icon: '🧩', color: '#E4C1F9' },
    { name: 'Paint', icon: '🎨', color: '#FFDAC1' },
    { name: 'Wood', icon: '🪵', color: '#FFF6BF' },
    { name: 'Steel', icon: '🔩', color: '#D3F8E2' },
    { name: 'Sand', icon: '🏖️', color: '#FFE0AC' },
    { name: 'Gravel', icon: '🪨', color: '#C4DFDF' },
    { name: 'Pipes', icon: '🧰', color: '#E0BBE4' },
    { name: 'Glass', icon: '🪟', color: '#C0F2EF' },
    { name: 'Concrete Blocks', icon: '🧊', color: '#E2F0CB' },
    { name: 'Roofing Sheets', icon: '🏠', color: '#FFB7B2' },
    { name: 'Adhesives', icon: '🧴', color: '#D5AAFF' },
    { name: 'Electrical Wire', icon: '🔌', color: '#F7C59F' },
    { name: 'Plumbing Fittings', icon: '🚰', color: '#D1E8E2' }
  ],
  Tools: [
    { name: 'Hammer', icon: '🔨', color: '#FFD6A5' },
    { name: 'Wrench', icon: '🔧', color: '#B5EAD7' },
    { name: 'Saw', icon: '🪚', color: '#E0F7FA' },
    { name: 'Drill Machine', icon: '🛠️', color: '#F2A2E8' }
  ],
  Services: [
    { name: 'Plumbing', icon: '🚿', color: '#D0F4DE' },
    { name: 'Electrician', icon: '⚡', color: '#DCEEF2' },
    { name: 'Bathroom', icon: '🛁', color: '#C2F0FC' },
    { name: 'Solar', icon: '🔆', color: '#FFF4D3' },
    { name: 'AC Service', icon: '❄️', color: '#DEF6FF' },
    { name: 'Carpentry', icon: '🪚', color: '#FFE5B4' },
    { name: 'Waterproofing', icon: '💧', color: '#E0F7E9' },
    { name: 'Painting Service', icon: '🖌️', color: '#FFF0F5' }
  ],
  Design: [
    { name: 'Architects', icon: '📐', color: '#FFD6E0' },
    { name: 'Designers', icon: '🖌️', color: '#CDEAC0' },
    { name: 'Furniture', icon: '🛋️', color: '#E0BBE4' },
    { name: 'Interior Decor', icon: '🪞', color: '#FFECB3' },
    { name: 'Modular Kitchen', icon: '🍽️', color: '#D1F2EB' },
    { name: 'Lighting', icon: '💡', color: '#FDFDBD' }
  ]
};

export default function HomePage() {
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('Materials');
  const [search, setSearch] = useState('');
  const [listening, setListening] = useState(false);
  const [stats, setStats] = useState({
  verifiedDealers:0,
  totalCategories:0,
  totalCustomers:0,
  averageRating:0,
  categoryCounts:{}
});
  const recognitionRef = useRef(null);

  const fetchLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          };
          setLocation(coords);
          resolve(coords);
        },
        (err) => {
          setError('Location access denied.');
          reject(err);
        }
      );
    });
  }, []);

  useEffect(() => {
    fetchLocation().catch(() => {});
  }, [fetchLocation]);

  useEffect(() => {
  http.get("/api/home/stats")
  .then((res)=>{
      setStats(res.data);
  })
  .catch(console.error);
},[]);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn("Speech Recognition not supported");
      return;
    }

    const recognition = new SpeechRecognition();

    recognition.lang = navigator.language || "en-IN"; // auto detect
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);

    recognition.onerror = (e) => {
      console.error("Speech error:", e);
      setListening(false);
    };

    recognition.onresult = async (event) => {
      const transcript = event.results[0][0].transcript.toLowerCase();
      setSearch(transcript);

      let currentLocation = location;

      const wantsNearby =
        transcript.includes("near me") ||
        transcript.includes("पास") ||
        transcript.includes("அருகில்") ||
        transcript.includes("ಹತ್ತಿರ") ||
        transcript.includes("അടുത്ത്");

      if (wantsNearby && !currentLocation) {
        try {
          currentLocation = await fetchLocation();
        } catch {
          return;
        }
      }

      let radius = null;
      const radiusMatch = transcript.match(/(\d+)\s?(km|kilometer|kilometers)/);
      if (radiusMatch) radius = radiusMatch[1];

      const allCategories = Object.values(categoryGroups).flat();
      const matched = allCategories.find(cat =>
        transcript.includes(cat.name.toLowerCase())
      );

      if (!matched) return;

      let sort = null;

      if (
        transcript.includes("cheap") ||
        transcript.includes("lowest") ||
        transcript.includes("budget") ||
        transcript.includes("ಸಸ್ತಾ") ||
        transcript.includes("കുറവ്")
      ) sort = "priceLow";

      if (
        transcript.includes("best") ||
        transcript.includes("top") ||
        transcript.includes("highest") ||
        transcript.includes("ಅತ್ಯುತ್ತಮ") ||
        transcript.includes("മികച്ച")
      ) sort = "ratingHigh";

      const params = new URLSearchParams();
      params.set("category", matched.name);

      if (currentLocation?.lat && currentLocation?.lng) {
        params.set("lat", currentLocation.lat);
        params.set("lng", currentLocation.lng);
      }

      if (sort) params.set("sort", sort);
      if (radius) params.set("radius", radius);

      navigate(`/dealers?${params.toString()}`);
    };

    recognitionRef.current = recognition;

  }, [location, navigate, fetchLocation]);

  const startListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.start();
    }
  };

  const handleCategoryClick = (category) => {
    const params = new URLSearchParams();
    params.set("category", category);

    if (location?.lat && location?.lng) {
      params.set("lat", location.lat);
      params.set("lng", location.lng);
    }

    navigate(`/dealers?${params.toString()}`);
  };

  const filteredCategories = categoryGroups[activeTab].filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
  <SEO
    title="BuildNear | Find Trusted Construction Material Dealers Near You"
    description="Find verified construction material suppliers, hardware stores, cement dealers, tile showrooms, steel suppliers and construction professionals near your location."
    keywords="construction materials, building materials, cement dealers, tile dealers, hardware stores, steel suppliers, construction suppliers India"
  url="https://buildnear.in/"
  />
    <div className="homepage-wrapper">

    <div className="hero-content">
      <div
        className="hero-section text-white text-center position-relative d-flex flex-column justify-content-center align-items-center"
        style={{
          backgroundImage: `linear-gradient(
                rgba(15,23,42,.75),
                rgba(15,23,42,.75)
              ),
              url('/images/hero/construction-hero.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight:'520px',
          padding:'80px 20px'
        }}
      >

        <div style={{ zIndex: 2, maxWidth: '720px' }}>
          <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="hero-title"
        >
          Find Trusted Construction Suppliers Near You
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="hero-subtitle"
        >
          Compare verified dealers, check inventory availability and get faster delivery.
        </motion.p>

         <div className="hero-badges">
          <span>✓ Verified Dealers</span>
          <span>✓ Live Inventory</span>
          <span>✓ Fast Delivery</span>
        </div> 

          <div className="search-wrapper">
              <input
                type="text"
                className="form-control"
                placeholder="Search categories or use voice..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <button
                className={`btn ${
                  listening ? "btn-danger" : "btn-warning"
                }`}
                onClick={startListening}
              >
                🎙
              </button>
            </div>

            <div className="popular-searches">
              <span>Bricks</span>
              <span>Cement</span>
              <span>Tiles</span>
              <span>Steel</span>
              <span>Paint</span>
            </div>
        </div>
      </div>
      </div>  
        <div className="trust-strip">

          <div className="trust-item">
            <h3>{stats.verifiedDealers || "500"}+</h3>
            <p>Verified Dealers</p>
          </div>

          <div className="trust-item">
            <h3>{stats.totalCategories || "50"}+</h3>
            <p>Categories</p>
          </div>

          <div className="trust-item">
            <h3>{stats.totalCustomers || "10K"}+</h3>
            <p>Customers</p>
          </div>

          <div className="trust-item">
            <h3>{stats.averageRating || "4.8"}★</h3>
            <p>Average Rating</p>
          </div>

        </div>

      <div className="container py-5">
        {error && <div className="alert alert-danger text-center">{error}</div>}

        <div className="d-flex justify-content-center mb-4 flex-wrap">
          {Object.keys(categoryGroups).map(group => (
            <button
              key={group}
                            className={`category-tab ${
                activeTab === group ? 'active' : ''
              }`}
              onClick={() => setActiveTab(group)}
            >
              {group}
            </button>
          ))}
        </div>

        <div className="row g-4">
          {filteredCategories.map((cat, i) => (
            <motion.div
              key={cat.name}
              className="col-6 col-sm-4 col-md-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div
                className="category-card h-100"
                onClick={() => handleCategoryClick(cat.name)}
              >
                <div className="card-top">
                  <div>
                    <h5 className="category-name">
                      {cat.name}
                    </h5>
                    <span className="category-badge">
                      Popular
                    </span>
                    <div className="dealer-count">
                      {stats.categoryCounts?.[cat.name] || 0} Verified Dealers
                    </div>
                  </div>

                  <div
                    className="category-icon"
                    style={{ backgroundColor: cat.color }}
                  >
                    {cat.icon}
                  </div>
                </div>

                <div className="category-bottom">
                  <span>Nearby Suppliers Available</span>

                  <p className="category-action">
                    Explore Dealers →
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-5 text-center">
          <p className="text-muted">Powered by your location. Optimized for speed.</p>
        </div>
      </div>
    </div>
    </>
  );
}